import styled, { css } from 'styled-components';

import Button from '@klv/atoms/button';
import Typography from '@klv/atoms/typography';
import { SelectArrow, SelectFieldGroup } from '@klv/molecules/form-field/select';
import { Input } from '@klv/molecules/form-field';
import Icon from '@klv/atoms/icon';
import Grid from '@klv/utils/grid';
import PlanCard from '../plan-card';

export const CloseButton = styled(Button)`
  margin: 3.6rem 0 0;

  ${({ theme }) => theme.screenSize.s1024.mediaQuery} {
    display: none;
  }
`;

export const StyledPricing = styled.div`
  padding-top: 2.4rem;
  padding-bottom: 4.8rem;

  ${({ theme }) => css`
    background-color: ${theme.pricing.background};
    color: ${theme.pricing.text};

    ${theme.screenSize.s1024.mediaQuery} {
      padding-top: 4.8rem;
      padding-bottom: 3.2rem;
    }
  `}
`;

export const DropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 2.4rem;
`;

export const HeaderContent = styled.div<{ $centered: boolean; }>`
  ${({ theme, $centered }) => `
    grid-column: ${$centered ? 'span 12' : 'span 8' };
    text-align: ${$centered ? 'center' : 'left' };

    ${theme.screenSize.s576.mediaQuery} {
      grid-column: span 12;
    }

    ${DropdownWrapper} {
      justify-content: ${$centered ? 'center' : 'flex-start' };
    }
  `}
`;

export const StyledEyebrow = styled(Typography)`
  color: inherit;
  line-height: 1.5;
  margin-bottom: 0.4rem;
`;

export const StyledHeadline = styled(Typography)`
  color: inherit;
  font-weight: 500;
`;

export const Header = styled.div<{ $stuck: boolean; }>`
  padding-top: 1px;
  position: sticky;
  top: -1px;
  z-index: 1;

  ${DropdownWrapper} {
    flex-direction: column;
    align-items: stretch;
    row-gap: 1.6rem;

    ${({ theme }) => `
      ${theme.screenSize.s1024.mediaQuery} {
        align-items: center;
        column-gap: 1.6rem;
        flex-direction: row;
      }
    `}
  }

  ${({ $stuck, theme }) => `
    ${theme.screenSize.s1024.mediaQuery} {
      position: static;
    }
    ${$stuck ? `
      z-index: 10;

      @media only screen and (max-width: ${(theme.screenSize.s1024.size * 10) - 1}px) {
        background-color: ${theme.pricing.stickyBackground};
        padding-top: 2.4rem;
        padding-bottom: 3.2rem;

        ${StyledEyebrow} {
          display: none;
        }

        ${StyledHeadline} {
          font-weight: 400;
        }
      }
    ` : ''}
  `}
`;

export const FlexContainer = styled.div`
  display: flex;
  margin: 12px 0;
`;

export const StyledLabel = styled(Typography)`
  flex-grow: 1;
  align-items: center;
  column-gap: 0.4rem;
  display: flex;
  font-size: 1.6rem;
  line-height: 1.5;
  text-align: left;
`;

export const Dropdown = styled(SelectFieldGroup)`
  flex-shrink: 0;

  ${Input} {
    border-width: 0.5px;
    cursor: pointer;
    font-size: 1.6rem;
    font-weight: 500;
    margin: 0;

    &:focus {
      outline-offset: 3px; 
      outline: 1px dashed currentColor;
    }

    ${({ theme }) => css`
      background-color: ${theme.pricing.dropdownBackground};
      border-color: ${theme.pricing.dropdownBorder};
      color: ${theme.pricing.dropdownText};
    `}
  }

  ${({ theme }) => css`
    ${SelectArrow} {
      color: ${theme.pricing.dropdownText};
      right: 0.9rem;
      top: 2.5rem;
    }
  `}
`;

export const AnchorLink = styled(Typography)`
  padding: 1.2rem 1.8rem;
  text-decoration: none;

  ${({ theme }) => `
    border-bottom: 1px solid ${theme.pricing.dropdownBorder};
  `}

  &:last-of-type {
    border-bottom: none;
  }
`;

export const AnchorLinks = styled.div`
  border-radius: 0.5rem;
  grid-column: span 8;
  display: flex;
  flex-direction: column;
  margin-bottom: 4rem;
  overflow: hidden;
  text-align: center;

  ${({ theme }) => `
    background: ${theme.pricing.cardBackground};
    border: 1px solid ${theme.pricing.dropdownBorder};
    ${theme.screenSize.s576.mediaQuery} {
      grid-column: span 12;
    }

    ${theme.screenSize.s1024.mediaQuery} {
      display: none;
    }
  `}

  ${AnchorLink} {
    font-weight: 500;
  }
`;

export const InformationIcon = styled(Icon)`
  display: inline-block;
  flex-shrink: 0;
`;

export const ModalTrigger = styled(Typography)`
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  color: inherit;
  column-gap: 0.4rem;
  cursor: pointer;
  display: flex;
  font-size: 1.6rem;
  line-height: 1.5;
  text-align: left;
`;

export const StyledPlanCard = styled(PlanCard)`
  ${({ theme }) => css`
    ${SelectArrow} {
      color: ${theme.pricing.dropdownText};
      right: 0.9rem;
      top: 2.5rem;
    }
  `}
`;

export const PlanCardDropdownWrapper = styled(DropdownWrapper)`
  column-gap: 1.2rem;
  margin-bottom: 1.6rem;

  ${Input} {
    padding-right: 3.6rem;
  }
`;

export const Plans = styled(Grid)`
  margin-top: 4.8rem;
  row-gap: 3.2rem;

  ${StyledPlanCard} {
    grid-column: span 8;

    ${({ theme }) => `
      ${theme.screenSize.s576.mediaQuery} {
        grid-column: span 12;
      }

      ${theme.screenSize.s1024.mediaQuery} {
        grid-column: span 4;
      }
    `}
  }
`;
export const StyledSpacer = styled.div`
  ${({ theme }) => `
      ${theme.screenSize.s576.mediaQuery} {
        display: none;
      }

      ${theme.screenSize.s1024.mediaQuery} {
        display: block;
        grid-column: span 2;
      }
  `}
`;
